/**
 * The backlog board: one column per status, cards by priority, in swimlanes by epic (an epic is
 * its lane's header, with its rolled-up status and "n of m done", never a card in its own lane).
 * Cards open the item drawer. They move between columns by dragging, by Shift with the left or
 * right arrow key on a focused card, or from the card's menu, which also starts or retries a
 * mission.
 *
 * Card: kind and key on a caption line, the title (two lines at most), one meta line (points or
 * roll-up, owner) and at most one chip: the attention pill when a mission waits on a person
 * ("Needs you" or "Blocked", the same resolver as the tree and table), or Critical for a critical
 * defect.
 *
 * Columns are sized so whole columns fit the view (between --board-column-min and -max); the rest
 * scroll a column at a time, with an edge fade and a scroll button saying more are there. Lane
 * headers stay put while the board scrolls sideways. On a phone the board shows one column at a
 * time, picked from a menu, and only the lanes that hold cards in it.
 */
import { ArrowRight, ChevronLeft, ChevronRight, Layers, MoreHorizontal, Plus, Rocket, RotateCcw, ShieldAlert } from '@orgus/ui/icons';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { ITEM_WORKFLOW, statusLabel } from '@orgus/protocol';
import type { BacklogItem, Id, ItemStatus, QualityIssue } from '@orgus/protocol';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Pressable,
  REVEAL_GROUP,
  RevealSlot,
  Select,
  Tooltip,
  cn,
} from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { ownerHint, ownersDiffer, startAbility } from '../requirements/model.ts';
import { KeyLabel, PriorityLevel, rollupText } from '../requirements/parts.tsx';
import { headlineMission, isActive } from './model.ts';
import { MissionStages } from './MissionStages.tsx';
import { missionStages } from './stages.ts';
import type { BoardColumn, MissionRow } from './model.ts';
import { ItemState, ItemStatusIcon, KindIcon, StatePill } from './parts.tsx';
import { AttentionChip, attentionOf } from './MissionBlockerChip.tsx';

export interface BoardProps {
  columns: BoardColumn[];
  allItems: readonly BacklogItem[];
  rows: readonly MissionRow[];
  issues?: ReadonlyMap<Id, QualityIssue[]>;
  projectName: (id: Id) => string;
  showProject: boolean;
  selectedId: Id | null;
  /** Group the cards into one lane per epic. */
  lanes?: boolean;
  onOpen: (id: Id) => void;
  onMove: (item: BacklogItem, status: ItemStatus) => void;
  onStart: (item: BacklogItem) => void;
  onRetry?: (row: MissionRow) => void;
  onStartSequence?: (item: BacklogItem) => void;
  onAdd: (status: ItemStatus) => void;
}

const DRAG_TYPE = 'application/x-orgus-item';

/** The status a card moves to with Shift and an arrow key: the next or previous one its kind has on the board. */
export function stepStatus(item: Pick<BacklogItem, 'kind' | 'status'>, shown: readonly ItemStatus[], dir: 1 | -1): ItemStatus | null {
  const flow = ITEM_WORKFLOW[item.kind].filter((s) => s !== 'dropped' && shown.includes(s));
  const i = flow.indexOf(item.status);
  const next = i < 0 ? null : flow[i + dir];
  return next ?? null;
}

/** The epic an item sits under, at any depth (itself when it is one). */
export function epicOf(item: BacklogItem, byId: ReadonlyMap<Id, BacklogItem>): BacklogItem | null {
  let cur: BacklogItem | undefined = item;
  for (let hops = 0; cur && hops < 50; hops++) {
    if (cur.kind === 'epic') return cur;
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return null;
}

export interface Lane {
  epic: BacklogItem | null;
  columns: BoardColumn[];
}

/** Cards grouped into one lane per epic (epics with cards first, by rank), then the cards with no epic. */
export function boardLanes(columns: readonly BoardColumn[], allItems: readonly BacklogItem[]): Lane[] {
  const byId = new Map(allItems.map((i) => [i.id, i]));
  const lanes = new Map<Id | '', Lane>();
  const laneFor = (epic: BacklogItem | null): Lane => {
    const key = epic?.id ?? '';
    let lane = lanes.get(key);
    if (!lane) {
      lane = { epic, columns: columns.map((c) => ({ ...c, items: [] })) };
      lanes.set(key, lane);
    }
    return lane;
  };
  columns.forEach((col, ci) => {
    for (const item of col.items) {
      if (item.kind === 'epic') {
        laneFor(item);
        continue;
      }
      laneFor(epicOf(item, byId)).columns[ci]!.items.push(item);
    }
  });
  return [...lanes.values()].sort((a, b) => Number(!a.epic) - Number(!b.epic) || (a.epic && b.epic ? a.epic.rank - b.epic.rank || a.epic.key.localeCompare(b.epic.key) : 0));
}

function Card({ item, p, showOwner, shown, onDragStart }: { item: BacklogItem; p: BoardProps; showOwner: boolean; shown: readonly ItemStatus[]; onDragStart: (item: BacklogItem) => void }) {
  const mission = headlineMission(p.rows, item.id);
  const ability = startAbility(item, p.rows, p.allItems);
  const selected = p.selectedId === item.id;
  const moves = ITEM_WORKFLOW[item.kind].filter((s) => s !== item.status);
  const progress = rollupText(item, p.allItems);
  // A mission waiting on a person reads "Needs you" (what it asks is the tooltip); an approval
  // that stopped it reads as the block (R-HL-1, R-HL-3). One resolver for tree, table and board.
  const chip =
    mission && attentionOf(mission.view) ? (
      <AttentionChip view={mission.view} />
    ) : item.defect?.severity === 'critical' ? (
      <StatePill tone="danger" icon={ShieldAlert} label="Critical" variant="soft" />
    ) : null;
  // Two meta facts at most, one with a chip beside them (layout/row-chips): size or roll-up
  // first, then the project, then the owner while there is room.
  const room = chip ? 1 : 2;
  // In lanes by epic the lane already names the project's epic, so the card leaves the project out.
  const facts = ([progress ?? (item.estimate != null ? `${item.estimate} pts` : null), p.showProject && !p.lanes ? p.projectName(item.projectId) : null].filter(Boolean) as string[]).slice(0, room);
  const owner = showOwner && item.owner ? item.owner : null;
  // The stage dots of the mission working on it (or waiting for review): where it is, at a glance.
  const stages = mission && (isActive(mission.view.state) || item.status === 'review') ? missionStages(mission.mission, mission.view) : null;
  const onKeyDown = (e: KeyboardEvent) => {
    if (!e.shiftKey || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
    const to = stepStatus(item, shown, e.key === 'ArrowRight' ? 1 : -1);
    if (!to) return;
    e.preventDefault();
    p.onMove(item, to);
  };
  return (
    <li
      className={cn('relative', REVEAL_GROUP)}
      draggable
      onDragStart={(e: DragEvent) => {
        e.dataTransfer.setData(DRAG_TYPE, item.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(item);
      }}
    >
      <Pressable
        title={showOwner ? undefined : ownerHint(item.owner)}
        onClick={() => p.onOpen(item.id)}
        onKeyDown={onKeyDown}
        aria-current={selected || undefined}
        aria-keyshortcuts="Shift+ArrowLeft Shift+ArrowRight"
        aria-description="Shift and an arrow key moves it to the next or previous column."
        data-item-card={item.id}
        className={cn('flex cursor-grab flex-col gap-2 rounded-card border bg-surface p-(--board-card-padding) hover:border-border-strong active:cursor-grabbing', selected ? 'border-accent' : 'border-border')}
      >
        <span className="flex items-center gap-2 pr-6">
          <KindIcon kind={item.kind} tooltip={false} />
          <KeyLabel value={item.key} />
          {/* Priority: a labelled mark with a tooltip, as in the table (the name is its aria-label). */}
          <span className="ml-auto flex items-center gap-2">
            {stages && <MissionStages stages={stages} variant="compact" />}
            <PriorityLevel priority={item.priority} />
          </span>
        </span>
        <span className="line-clamp-2 min-w-0 text-ui font-medium text-fg">{item.title}</span>
        {(facts.length > 0 || chip || owner) && (
          <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
            {facts.map((f) => (
              <span key={f} className="min-w-0 truncate num" data-meta>
                {f}
              </span>
            ))}
            {chip}
            {owner && (
              <Tooltip content={`Owner: ${owner}`}>
                <span tabIndex={-1} className="ml-auto inline-flex shrink-0">
                  <Avatar name={owner} size={20} />
                </span>
              </Tooltip>
            )}
          </span>
        )}
      </Pressable>
      <RevealSlot className="absolute right-2 top-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label={`Actions for ${item.key}`} icon={<MoreHorizontal />} size="xs" noTooltip />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {ability.kind === 'mission' && (
              <DropdownMenuItem icon={<Rocket />} onSelect={() => p.onStart(item)}>
                Start mission…
              </DropdownMenuItem>
            )}
            {ability.kind === 'retry' && (
              <DropdownMenuItem icon={<RotateCcw />} onSelect={() => (p.onRetry ? p.onRetry(ability.row) : p.onStart(item))}>
                Retry mission…
              </DropdownMenuItem>
            )}
            {ability.kind === 'sequence' && p.onStartSequence && (
              <DropdownMenuItem icon={<Layers />} onSelect={() => p.onStartSequence!(item)}>
                Run as a sequence…
              </DropdownMenuItem>
            )}
            {ability.kind === 'running' && <DropdownMenuLabel>A mission is working on it</DropdownMenuLabel>}
            {ability.kind !== 'none' && <DropdownMenuSeparator />}
            <DropdownMenuLabel>Move to</DropdownMenuLabel>
            {moves.map((s) => (
              <DropdownMenuItem key={s} icon={<ArrowRight />} onSelect={() => p.onMove(item, s)}>
                {statusLabel(item.kind, s)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </RevealSlot>
    </li>
  );
}

/** One column's cards; a drop target for any card whose kind has this status. */
function Cell({ col, p, showOwner, shown, dragging, setDragging, lane }: { col: BoardColumn; p: BoardProps; showOwner: boolean; shown: readonly ItemStatus[]; dragging: BacklogItem | null; setDragging: (i: BacklogItem | null) => void; lane: string }) {
  const [over, setOver] = useState(false);
  const accepts = !!dragging && dragging.status !== col.status && ITEM_WORKFLOW[dragging.kind].includes(col.status);
  return (
    <ul
      className={cn('flex min-h-row flex-col gap-(--board-card-gap) rounded-card', over && accepts && 'outline-2 outline-offset-2 outline-accent')}
      aria-label={`${col.title}${lane ? ` in ${lane}` : ''}`}
      onDragOver={(e) => {
        if (!accepts) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        if (!accepts || !dragging) return;
        e.preventDefault();
        p.onMove(dragging, col.status);
        setDragging(null);
      }}
    >
      {col.items.map((item) => (
        <Card key={item.id} item={item} p={p} showOwner={showOwner} shown={shown} onDragStart={setDragging} />
      ))}
      {/* An empty cell stays empty at rest (a placeholder in every lane is clutter); it shows a drop target while a card is dragged. */}
      {!col.items.length && accepts && <li className="flex h-row items-center justify-center rounded-card border border-dashed border-border text-caption text-fg-subtle">Drop here</li>}
    </ul>
  );
}

function ColumnHeader({ col, p }: { col: BoardColumn; p: BoardProps }) {
  return (
    <header className="flex h-row items-center gap-2 px-2" data-group-header>
      <ItemStatusIcon status={col.status} />
      <h2 className="text-ui font-medium text-fg">{col.title}</h2>
      <span className="text-caption text-fg-subtle num">{col.items.length}</span>
      <IconButton label={`New item in ${col.title}`} icon={<Plus />} size="xs" className="ml-auto" onClick={() => p.onAdd(col.status)} />
    </header>
  );
}

/** How many whole columns of at least `min` fit in `view`, and the width that fills it with them. */
export function fitColumns(view: number, count: number, gap: number, min: number, max: number): { n: number; width: number } {
  if (count <= 0 || view <= 0) return { n: Math.max(count, 0), width: min };
  const n = Math.min(count, Math.max(1, Math.floor((view + gap) / (min + gap))));
  return { n, width: Math.min(max, Math.floor((view - (n - 1) * gap) / n)) };
}

/** What lies past each edge of the board, and how wide each fade must be to hide every part-column. */
export interface BoardEdges {
  before: boolean;
  after: boolean;
  /** From the scroller's left edge to the first whole column. */
  left: number;
  /** From the last whole column to the scroller's right edge. */
  right: number;
}

/**
 * Pure: the fades at the board's edges cover exactly the space outside the whole columns in view,
 * so a part-column never shows a sliced card; at least `min` wide so the scroll button fits.
 */
export function boardEdges(scrollLeft: number, view: number, scrollWidth: number, step: number, width: number, min = EDGE_GUTTER): BoardEdges {
  const before = scrollLeft > 1;
  const after = scrollLeft + view < scrollWidth - 1;
  if (step <= 0 || width <= 0) return { before, after, left: min, right: min };
  const first = Math.ceil((scrollLeft - 1) / step);
  const last = Math.floor((scrollLeft + view - width + 1) / step);
  const left = before ? Math.max(min, first * step - scrollLeft) : 0;
  const right = after ? Math.max(min, scrollLeft + view - (Math.max(last, first) * step + width)) : 0;
  return { before, after, left: Math.round(left), right: Math.round(right) };
}

/**
 * Sizes the columns so whole columns fit the scroller, and tracks what lies to either side.
 * The widths go out as CSS variables (--board-fit, the column; --board-view, the visible width).
 */
function useFittedColumns(count: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ width: number; view: number; step: number } | null>(null);
  const fitRef = useRef(fit);
  fitRef.current = fit;
  const [edges, setEdges] = useState<BoardEdges>({ before: false, after: false, left: 0, right: 0 });
  const measureEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const f = fitRef.current;
    const next = boardEdges(el.scrollLeft, el.clientWidth, el.scrollWidth, f?.step ?? 0, f?.width ?? 0);
    setEdges((e) => (e.before === next.before && e.after === next.after && e.left === next.left && e.right === next.right ? e : next));
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const css = getComputedStyle(el);
      const row = el.matches('[data-board-row]') ? el : el.querySelector<HTMLElement>('[data-board-row]');
      const gap = row ? parseFloat(getComputedStyle(row).columnGap) || 0 : 0;
      const probe = (name: string, fallback: number) => {
        const v = parseFloat(css.getPropertyValue(name));
        return Number.isFinite(v) && v > 0 ? v : fallback;
      };
      const min = probe('--board-column-min', 272);
      const max = probe('--board-column-max', 360);
      // When some columns lie beyond the view, keep a gutter at both edges for the fade and its
      // scroll button (the scroller's scroll padding keeps the left one), so the button never sits
      // on a column header's own controls.
      const all = fitColumns(el.clientWidth, count, gap, min, max);
      const width = all.n < count ? Math.floor((el.clientWidth - 2 * EDGE_GUTTER - (all.n - 1) * gap) / all.n) : all.width;
      const next = { width, view: el.clientWidth, step: width + gap };
      fitRef.current = next;
      setFit((f) => (f && f.width === width && f.view === el.clientWidth ? f : next));
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count, measureEdges]);
  // Once the columns take their new width, the fades follow it.
  useLayoutEffect(measureEdges, [fit, measureEdges]);
  // --board-edge: where the left fade ends, so a sticky lane header starts clear of it.
  const style = { ...(fit ? { '--board-fit': `${fit.width}px`, '--board-view': `${fit.view}px` } : { '--board-fit': 'var(--board-column-width)', '--board-view': '100%' }), '--board-edge': `${edges.left}px` } as CSSProperties;
  const scrollBy = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * (fit?.step ?? 296), behavior: 'smooth' });
  return { ref, style, edges, onScroll: measureEdges, scrollBy };
}

/** The least width of an edge fade (and the scroller's scroll-pl-12) that holds the scroll button. */
const EDGE_GUTTER = 48;

/** The fades and the scroll buttons at the board's edges while columns lie beyond them. */
function ScrollEdges({ edges, scrollBy }: { edges: BoardEdges; scrollBy: (dir: 1 | -1) => void }) {
  const widths = { '--fade-left': `${edges.left}px`, '--fade-right': `${edges.right}px` } as CSSProperties;
  return (
    <>
      {edges.before && (
        <div style={widths} className="pointer-events-none absolute inset-y-0 left-0 z-(--z-sticky) flex w-(--fade-left) items-start justify-start bg-linear-to-r from-bg from-75% to-transparent pt-2" data-fade="left">
          <IconButton label="Show earlier columns" icon={<ChevronLeft />} size="xs" className="pointer-events-auto" onClick={() => scrollBy(-1)} />
        </div>
      )}
      {edges.after && (
        <div style={widths} className="pointer-events-none absolute inset-y-0 right-0 z-(--z-sticky) flex w-(--fade-right) items-start justify-end bg-linear-to-l from-bg from-75% to-transparent pt-2" data-fade="right">
          <IconButton label="Show more columns" icon={<ChevronRight />} size="xs" className="pointer-events-auto" onClick={() => scrollBy(1)} />
        </div>
      )}
    </>
  );
}

const laneName = (lane: Lane) => (lane.epic ? `${lane.epic.key} ${lane.epic.title}` : 'No epic');

/** A lane's header: the epic (key, title, rolled-up status, "n of m done"), or "No epic". */
function LaneHeader({ lane, p, sticky }: { lane: Lane; p: BoardProps; sticky?: boolean }) {
  const name = laneName(lane);
  const done = lane.epic ? rollupText(lane.epic, p.allItems) : null;
  return (
    <header className="flex h-row items-center border-b border-border-subtle" data-group-header>
      {/* Sticky to the scroller's left edge and as wide as the view at most: the key and title stay whole while the board scrolls. */}
      <span className={cn('flex min-w-0 items-center gap-3 px-2', sticky && 'sticky left-(--board-edge) max-w-(--board-view)')}>
        {lane.epic ? (
          <Pressable onClick={() => p.onOpen(lane.epic!.id)} className="flex w-auto min-w-0 items-center gap-2 rounded-control hover:text-fg" aria-label={`Open epic ${name}`}>
            <KindIcon kind="epic" tooltip={false} />
            <KeyLabel value={lane.epic.key} />
            <span className="truncate text-ui font-medium text-fg">{lane.epic.title}</span>
          </Pressable>
        ) : (
          <span className="text-ui font-medium text-fg-muted">No epic</span>
        )}
        {lane.epic && <ItemState status={lane.epic.status} kind="epic" className="shrink-0 max-sm:hidden" />}
        {done && (
          <span className="shrink-0 text-caption text-fg-subtle num" data-meta>
            {done}
          </span>
        )}
      </span>
    </header>
  );
}

interface PhoneBoardProps {
  p: BoardProps;
  counted: BoardColumn[];
  lanes: Lane[] | null;
  showOwner: boolean;
  shown: readonly ItemStatus[];
  dragging: BacklogItem | null;
  setDragging: (i: BacklogItem | null) => void;
}

/** A phone: one column at a time, picked from a menu, and only the lanes with cards in it. */
function PhoneBoard({ p, counted, lanes, showOwner, shown, dragging, setDragging }: PhoneBoardProps) {
  const [picked, setPicked] = useState<ItemStatus | undefined>(() => counted.find((c) => c.items.length)?.status);
  const col = counted.find((c) => c.status === picked) ?? counted[0];
  if (!col) return null;
  const ci = counted.indexOf(col);
  const inLanes = lanes ? lanes.map((l) => ({ lane: l, col: l.columns[ci]! })).filter((x) => x.col.items.length) : null;
  return (
    <div className="flex min-w-0 flex-col gap-3" onDragEnd={() => setDragging(null)}>
      <div className="flex min-w-0 items-center gap-2">
        <Select aria-label="Board column" size="sm" value={col.status} onValueChange={(v) => setPicked(v as ItemStatus)} options={counted.map((c) => ({ value: c.status, label: `${c.title} (${c.items.length})` }))} wrapperClassName="min-w-0 flex-1" />
        <IconButton label={`New item in ${col.title}`} icon={<Plus />} size="xs" onClick={() => p.onAdd(col.status)} />
      </div>
      <div role="list" aria-label={`Backlog board, ${col.title}`} className="flex flex-col gap-3">
        {!col.items.length && <p className="px-2 py-3 text-caption text-fg-subtle">Nothing in {col.title}</p>}
        {inLanes
          ? inLanes.map(({ lane, col: lc }) => (
              <div key={lane.epic?.id ?? 'none'} role="listitem" aria-label={laneName(lane)} className="flex flex-col gap-2">
                <LaneHeader lane={lane} p={p} />
                <Cell col={lc} p={p} showOwner={showOwner} shown={shown} dragging={dragging} setDragging={setDragging} lane={laneName(lane)} />
              </div>
            ))
          : col.items.length > 0 && (
              <div role="listitem" aria-label={`${col.title}, ${col.items.length} items`}>
                <Cell col={col} p={p} showOwner={showOwner} shown={shown} dragging={dragging} setDragging={setDragging} lane="" />
              </div>
            )}
      </div>
    </div>
  );
}

export function Board(p: BoardProps) {
  const showOwner = ownersDiffer(p.columns.flatMap((c) => c.items.map((i) => i.owner)));
  const shown = p.columns.map((c) => c.status);
  const [dragging, setDragging] = useState<BacklogItem | null>(null);
  const lanes = useMemo(() => (p.lanes ? boardLanes(p.columns, p.allItems) : null), [p.lanes, p.columns, p.allItems]);
  const counted = p.lanes ? p.columns.map((c) => ({ ...c, items: c.items.filter((i) => i.kind !== 'epic') })) : p.columns;
  const phone = useMediaQuery(NARROW_QUERY);
  const fit = useFittedColumns(counted.length);
  const oneLane = !lanes || (lanes.length === 1 && !lanes[0]!.epic);

  if (phone) return <PhoneBoard p={p} counted={counted} lanes={oneLane ? null : lanes} showOwner={showOwner} shown={shown} dragging={dragging} setDragging={setDragging} />;

  // Snap to column starts, so a scroll stops on a column edge, never inside a card.
  const scroller = 'min-w-0 snap-x snap-mandatory scroll-pl-12 overflow-x-auto pb-2';
  if (oneLane) {
    return (
      <div className="relative min-w-0">
        <div ref={fit.ref} style={fit.style} onScroll={fit.onScroll} className={cn(scroller, 'flex gap-(--board-column-gap)')} data-board-row role="list" aria-label="Backlog board" onDragEnd={() => setDragging(null)}>
          {counted.map((col) => (
            <div key={col.status} role="listitem" aria-label={`${col.title}, ${col.items.length} items`} className="flex w-(--board-fit) shrink-0 snap-start flex-col gap-2 rounded-card bg-(--board-column-bg) p-2">
              <ColumnHeader col={col} p={p} />
              <Cell col={col} p={p} showOwner={showOwner} shown={shown} dragging={dragging} setDragging={setDragging} lane="" />
            </div>
          ))}
        </div>
        <ScrollEdges edges={fit.edges} scrollBy={fit.scrollBy} />
      </div>
    );
  }

  return (
    <div className="relative min-w-0">
      <div ref={fit.ref} style={fit.style} onScroll={fit.onScroll} className={scroller} onDragEnd={() => setDragging(null)}>
        <div className="flex w-max min-w-full flex-col gap-3" role="list" aria-label="Backlog board, one lane per epic">
          {/* The column headers hold "New item in ..." buttons, so they stay in the accessibility
              tree (aria-hidden over focusable buttons hid them from screen readers only). */}
          <div className="flex gap-(--board-column-gap)" data-board-row role="listitem" aria-label="Columns">
            {counted.map((col) => (
              <div key={col.status} className="w-(--board-fit) shrink-0 snap-start rounded-card bg-(--board-column-bg) px-2">
                <ColumnHeader col={col} p={p} />
              </div>
            ))}
          </div>
          {lanes!.map((lane) => (
            <div key={lane.epic?.id ?? 'none'} role="listitem" aria-label={laneName(lane)} className="flex flex-col gap-2">
              <LaneHeader lane={lane} p={p} sticky />
              <div className="flex gap-(--board-column-gap)">
                {lane.columns.map((col) => (
                  <div key={col.status} className="w-(--board-fit) shrink-0 rounded-card bg-(--board-column-bg) p-2">
                    <Cell col={col} p={p} showOwner={showOwner} shown={shown} dragging={dragging} setDragging={setDragging} lane={laneName(lane)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <ScrollEdges edges={fit.edges} scrollBy={fit.scrollBy} />
    </div>
  );
}
